import { marked } from 'marked';

/**
 * CANONICAL MARKED.JS CONFIGURATION
 * 
 * This configuration is used everywhere markdown is parsed to ensure:
 * 1. No HTML entity encoding (quotes stay as quotes, not &quot;)
 * 2. No smart quote conversion
 * 3. GitHub Flavored Markdown support
 * 4. Consistent newline handling
 * 
 * USAGE:
 *   import { parseMarkdownToTokens } from 'shared/config/markedConfig';
 *   const tokens = parseMarkdownToTokens(markdown);
 */

/**
 * 🔧 CUSTOM: Post-process tokens to convert indented code blocks to paragraphs
 * 
 * REASON: We want indentation (tabs, 4+ spaces) to create visually indented paragraphs,
 * not code blocks. This overrides the CommonMark standard where 4 spaces = code block.
 * 
 * IMPACT:
 * - ✅ Tabs → Indented paragraphs
 * - ✅ 4+ spaces → Indented paragraphs  
 * - ✅ Triple backticks (```) → Still work perfectly for code blocks
 * - ❌ 4-space code blocks → Converted to paragraphs (must use ```)
 * 
 * This deviates from markdown standard but provides better UX for document editing.
 */
function convertIndentedCodeBlocksToParagraphs(tokens: any[], inputHadTabs: boolean = false): any[] {
  return tokens.flatMap(token => {
    // Check if this is a code block WITHOUT explicit language (likely indented code)
    if (token.type === 'code' && (!token.lang || token.lang.trim() === '')) {
      // ✅ BETTER: Use codeBlockStyle property if available, fallback to raw check
      const isIndentedSyntax = token.codeBlockStyle === 'indented' || (token.raw && !token.raw.startsWith('```'));
      
      if (isIndentedSyntax) {
        // ✅ CRITICAL FIX: Extract leading whitespace from token.raw (marked.js strips it from token.text!)
        // ⚠️ MARKED.JS BUG: marked.lexer EXPANDS tabs to 4 spaces per CommonMark spec
        // We need to UNDO this conversion when input had tabs
        const leadingWhitespaceMatch = token.raw.match(/^(\s+)/);
        let leadingSpaces = leadingWhitespaceMatch ? leadingWhitespaceMatch[1] : '';
        
        // 🔧 CRITICAL FIX: Convert spaces to tabs for indented paragraphs
        // BEHAVIOR: Always convert multiples of 4 spaces to tabs for paragraph indentation
        // REASON: 
        //   - 4 spaces = semantic paragraph indent (should be tab)
        //   - marked.lexer expands tabs to 4 spaces (we reverse this)
        //   - Even if AI uses 4 spaces, we normalize to tabs
        // POLICY: Paragraph indentation = tabs (4 spaces → 1 tab, 8 spaces → 2 tabs, etc.)
        if (leadingSpaces.length > 0 && /^ +$/.test(leadingSpaces)) {
          const spaceCount = leadingSpaces.length;
          
          // Check if it's a multiple of 4 (likely expanded tabs)
          if (spaceCount % 4 === 0) {
            const tabCount = spaceCount / 4;
            const restoredTabs = '\t'.repeat(tabCount);
            
            // Normalized spaces to tabs for paragraph indentation
            
            leadingSpaces = restoredTabs;
          }
        }
        
        // 🔧 CRITICAL FIX: Split on blank lines (multiple indented paragraphs)
        // marked.lexer treats multiple indented paragraphs as ONE code block
        // We need to split them back into separate paragraphs
        // IMPORTANT: marked.lexer STRIPS indentation from all but first line!
        const paragraphTexts = (token.text || '').split(/\n\n+/);  // Split text (no indentation)
        
        // Converting indented code block to paragraph(s)
        
        // Return array of paragraph tokens (flatMap will flatten)
        // ✅ CRITICAL: Prepend indentation to EACH split paragraph
        return paragraphTexts.map((text: string) => {
          // 🔧 CLEANUP: Remove any extra spaces after the tab indentation
          // AI sometimes adds "\t\t    Text" (tabs + spaces) when it should be "\t\tText"
          // We want ONLY the tab indentation, not extra spacing
          const cleanedText = text.replace(/^\s+/, '');  // Strip any leading spaces from text
          const finalText = leadingSpaces + cleanedText;  // Add back ONLY the tab indentation
          
          // 🎨 CRITICAL FIX: Re-tokenize inline content to properly detect HTML tags
          // PROBLEM: Creating a manual 'text' token traps HTML as literal text
          // SOLUTION: Use marked.Lexer.lexInline() to parse inline content properly
          // RESULT: HTML tags like <span style="..."> become 'html' tokens, not 'text' tokens
          let inlineTokens: any[];
          
          // 🛡️ EDGE CASE: Handle empty or whitespace-only text
          if (!cleanedText || cleanedText.trim() === '') {
            inlineTokens = [];  // Empty paragraph - no tokens needed
          } else {
            try {
              // Parse inline content - this will detect HTML, links, emphasis, code, etc.
              // marked.Lexer.lexInline() properly tokenizes:
              //   - HTML tags: <span style="..."> → type: 'html'
              //   - Bold/italic: **text** → type: 'strong'
              //   - Links: [text](url) → type: 'link'
              //   - Code: `code` → type: 'codespan'
              //   - Plain text: text → type: 'text'
              inlineTokens = marked.Lexer.lexInline(cleanedText);
              
              // 🛡️ VALIDATION: Ensure we got valid tokens
              if (!Array.isArray(inlineTokens) || inlineTokens.length === 0) {
                throw new Error('lexInline returned empty or invalid result');
              }
              
              // HTML tokens detected and will be processed
              
              // 🔧 CRITICAL FIX: Preserve indentation by prepending to first token
              // PROBLEM: When we have HTML tokens, the indentation is in paragraph.raw but not in the tokens
              // SOLUTION: Prepend leading spaces (tabs) to the first token so downstream processing preserves it
              // REASON: flattenInline() processes tokens array, not paragraph.raw/text
              if (leadingSpaces && inlineTokens.length > 0) {
                const firstToken = inlineTokens[0];
                
                // Update the first token's raw field to include leading indentation
                firstToken.raw = leadingSpaces + (firstToken.raw || '');
                
                // For text tokens, also update the text field
                // For HTML tokens, only raw matters (text is the same as raw)
                if (firstToken.type === 'text' && firstToken.text !== undefined) {
                  firstToken.text = leadingSpaces + firstToken.text;
                }
                
                // Indentation prepended to first token
              }
            } catch (error) {
              // 🛡️ FALLBACK: If inline parsing fails, create simple text token
              // This ensures we never lose content even if parsing fails
              console.warn('⚠️ [MARKED_CONFIG] Inline tokenization failed, falling back to text token:', {
                error: error instanceof Error ? error.message : String(error),
                textPreview: cleanedText.substring(0, 50),
                textLength: cleanedText.length
              });
              inlineTokens = [{
                type: 'text',
                raw: cleanedText,
                text: cleanedText
              }];
            }
          }
          
          return {
            type: 'paragraph',
            raw: finalText,
            text: finalText,
            tokens: inlineTokens  // ✅ Proper inline tokens (HTML, text, links, etc.)
          };
        });
      }
    }
    
    // Keep token unchanged (including fenced code blocks with ```)
    return token;  // flatMap will wrap single values automatically
  });
}

export const CANONICAL_MARKED_LEXER_OPTIONS = {
  gfm: true,           // GitHub Flavored Markdown (tables, strikethrough, etc.)
  breaks: false,       // Don't convert single \n to <br> (prevents extra line breaks)
  sanitize: false,     // 🔧 CRITICAL: Don't HTML-encode entities (" stays ", not &quot;)
  smartypants: false,  // 🔧 CRITICAL: Don't convert quotes to smart quotes
  xhtml: false,        // 🔧 CRITICAL: Don't use XHTML encoding
  pedantic: false,     // Use modern/forgiving parsing
  smartLists: true     // Better list number handling
};

/**
 * Parse markdown to tokens (recommended for document operations)
 * 
 * @param markdown Raw markdown string
 * @returns Token array from marked.lexer()
 */
export function parseMarkdownToTokens(markdown: string): any[] {
  // 🔧 FIX: marked.lexer requires trailing newline for proper block parsing
  // Without it, "## Heading" at EOF is treated as paragraph text instead of heading
  const markdownForLexer = markdown.endsWith('\n') ? markdown : markdown + '\n';
  
  // 🔍 DETECT: Check if input has tabs (before marked.lexer expands them)
  const inputHasTabs = /^\t/m.test(markdownForLexer);  // Multiline: check if any line starts with tab
  
  // Parse with marked
  const tokens = marked.lexer(markdownForLexer, CANONICAL_MARKED_LEXER_OPTIONS);
  
  // 🔧 CUSTOM: Convert indented code blocks to paragraphs (preserve indentation)
  // Pass info about whether input had tabs so we can restore them
  const processedTokens = convertIndentedCodeBlocksToParagraphs(tokens, inputHasTabs);
  
  // Paragraph tokens processed and ready
  
  return processedTokens;
}

/**
 * Parse markdown to HTML (for paste operations only)
 * 
 * @param markdown Raw markdown string
 * @returns HTML string from marked.parse()
 */
export function parseMarkdownToHTML(markdown: string): string {
  const html = marked.parse(markdown, CANONICAL_MARKED_LEXER_OPTIONS);
  return typeof html === 'string' ? html : String(html);
}

